import React, { useState } from 'react';
import { Box, Button, IconButton, Menu, MenuItem, ListItemIcon } from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreHoriz as MoreIcon,
  Groups as GroupsIcon,
  Person as PersonIcon,
  DirectionsRun as RunIcon,
  DirectionsBike as RideIcon,
  Pool as SwimIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Contest } from '../../models/types';
import { BreadcrumbTrail } from '../ui/BreadcrumbTrail';
import { StatusPill } from '../ui/StatusPill';
import { getContestStatus } from './contestFormat';

interface ContestHeaderProps {
  contest: Contest;
  groupName: string | null;
  canManage: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

const ACTIVITY_ICONS: Record<string, React.ReactNode> = { Run: <RunIcon />, Ride: <RideIcon />, Swim: <SwimIcon /> };

export const ContestHeader = ({ contest, groupName, canManage, onEdit, onDelete }: ContestHeaderProps) => {
  const { t } = useTranslation('contests');
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const status = getContestStatus(contest);

  return (
    <>
      <BreadcrumbTrail
        items={[
          { label: t('header.breadcrumbGroups'), to: '/groups' },
          { label: groupName || t('header.breadcrumbGroup'), to: `/groups/${contest.groupId}` },
          { label: contest.name },
        ]}
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 0, flex: '1 1 280px' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <StatusPill tone={status.tone} label={status.label} dot />
            <StatusPill tone="outline" icon={contest.contestType === 'Team' ? <GroupsIcon /> : <PersonIcon />} label={t(`contestType.${contest.contestType || 'Team'}`)} />
            <StatusPill tone="outline" icon={ACTIVITY_ICONS[contest.activityType] ?? <RunIcon />} label={t(`activityType.${contest.activityType || 'Run'}`)} />
          </Box>
          <Box component="h1" sx={{ m: 0, fontSize: 28, fontWeight: 600, letterSpacing: '-0.025em', lineHeight: 1.2, overflowWrap: 'anywhere' }}>
            {contest.name}
          </Box>
          {contest.detail && (
            <Box sx={{ fontSize: 14, color: 'var(--muted)', maxWidth: 560, lineHeight: 1.5, whiteSpace: 'pre-line' }}>{contest.detail}</Box>
          )}
        </Box>
        {canManage && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={onEdit}>
              {t('common:actions.edit')}
            </Button>
            <IconButton aria-label={t('header.moreActions')} onClick={e => setMenuAnchor(e.currentTarget)} sx={{ width: 36, height: 36, border: '1px solid var(--border)', borderRadius: '8px', backgroundColor: 'var(--card)' }}>
              <MoreIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  onDelete();
                }}
                sx={{ color: 'var(--danger)' }}
              >
                <ListItemIcon sx={{ color: 'inherit' }}>
                  <DeleteIcon fontSize="small" />
                </ListItemIcon>
                {t('header.deleteContest')}
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Box>
    </>
  );
};
